import { createContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { To } from "react-router";
import { DIALOG_GEOEFFNET, mitteilungZiel } from "./dialog";
import { fehlertext } from "./fehler";
import { db, supabase } from "./supabase";
import { useHinweis } from "./ui/Hinweise";
import { heute, tageBis } from "./zeit";

/* Mitteilungen in der App (KOPFZEILE.md): Zahl für die Glocke, Liste für Leiste und Seite, gelesen setzen.
   Die auslösende Person steht nicht in app_mitteilungen, sondern im Ereignis dazu (app_ereignisse.person, RLS wie im Verlauf). */

export const MITTEILUNGEN_PFAD = "/mitteilungen";

export interface Mitteilung {
  id: number;
  titel: string;
  text: string;
  link: string | null;
  gelesen_am: string | null;
  erstellt_am: string;
  /** Wer die Mitteilung ausgelöst hat. Ohne Person (Frist, Wochenübersicht) oder Ereignis nicht sichtbar: null */
  ausloeser: string | null;
}

export type Filter = "alle" | "ungelesen";

/** Zahl der ungelesenen Mitteilungen für die Glocke, jede Minute neu */
export function useUngelesen(): number {
  const q = useQuery({
    queryKey: ["mitteilungen", "ungelesen"],
    refetchInterval: 60_000,
    queryFn: async () => {
      if (!supabase) return 0;
      const { count, error } = await supabase.from("app_mitteilungen").select("id", { count: "exact", head: true }).is("gelesen_am", null);
      if (error) throw error;
      return count ?? 0;
    },
  });
  return q.data ?? 0;
}

/** Die letzten 100 Mitteilungen mit der auslösenden Person. RLS liefert nur die eigenen in der App. */
export function useMitteilungen() {
  return useQuery({
    queryKey: ["mitteilungen", "liste"],
    refetchInterval: 60_000,
    queryFn: async (): Promise<Mitteilung[]> => {
      const { data, error } = await db()
        .from("app_mitteilungen")
        .select("id, titel, text, link, gelesen_am, erstellt_am, ereignis:app_ereignisse(person)")
        .order("erstellt_am", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data.map(({ ereignis, ...m }) => ({ ...m, ausloeser: ereignis?.person ?? null }));
    },
  });
}

/** Genau eine Mitteilung gelesen setzen. Sofort in der Liste und an der Glocke, die Datenbank folgt. Schlägt es fehl, kommt der
 *  alte Stand zurück. */
export function useEineGelesen() {
  const qc = useQueryClient();
  const { zeigen } = useHinweis();
  return useMutation({
    mutationFn: async (id: number) => {
      const { error } = await db().from("app_mitteilungen").update({ gelesen_am: new Date().toISOString() }).eq("id", id).is("gelesen_am", null);
      if (error) throw error;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["mitteilungen"] });
      const jetzt = new Date().toISOString();
      qc.setQueryData<Mitteilung[]>(["mitteilungen", "liste"], (alt) => alt?.map((m) => (m.id === id && !m.gelesen_am ? { ...m, gelesen_am: jetzt } : m)));
      qc.setQueryData<number>(["mitteilungen", "ungelesen"], (n) => Math.max(0, (n ?? 1) - 1));
    },
    onError: (e) => zeigen(fehlertext(e), { fehler: true }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["mitteilungen"] }),
  });
}

export function useAlleGelesen() {
  const qc = useQueryClient();
  const { zeigen } = useHinweis();
  return useMutation({
    mutationFn: async () => {
      const { error } = await db().from("app_mitteilungen").update({ gelesen_am: new Date().toISOString() }).is("gelesen_am", null);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["mitteilungen"] }),
    onError: (e) => zeigen(fehlertext(e), { fehler: true }),
  });
}

/** Vorlesetext der Glocke */
export function glockeText(ungelesen: number): string {
  return ungelesen > 0 ? `Mitteilungen, ${ungelesen} ungelesen` : "Mitteilungen, keine ungelesen";
}

/** Zahl in der Marke der Glocke. Ab 100 reicht „99+“. */
export function glockeZahl(ungelesen: number): string {
  return ungelesen > 99 ? "99+" : String(ungelesen);
}

export function gefiltert<T extends Pick<Mitteilung, "gelesen_am">>(liste: readonly T[], filter: Filter): T[] {
  return filter === "ungelesen" ? liste.filter((m) => !m.gelesen_am) : [...liste];
}

/** Gruppen „Neu“ (ungelesen) und „Früher“ (gelesen), je in der Reihenfolge der Liste */
export function gruppiert<T extends Pick<Mitteilung, "gelesen_am">>(liste: readonly T[]): { neu: T[]; frueher: T[] } {
  return { neu: liste.filter((m) => !m.gelesen_am), frueher: liste.filter((m) => !!m.gelesen_am) };
}

const UHR = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });

/** Zeit einer Mitteilung in Berliner Zeit: „vor 12 Min.“, „vor 2 Std.“, „heute 08:00“, „gestern 14:10“, „13.10.“, „13.10.2025“ */
export function mitteilungZeit(iso: string, jetzt = new Date()): string {
  const t = new Date(iso);
  const minuten = Math.floor((jetzt.getTime() - t.getTime()) / 60_000);
  const tag = heute(t);
  const abstand = tageBis(tag, heute(jetzt));
  if (minuten < 1) return "gerade eben";
  if (minuten < 60) return `vor ${minuten} Min.`;
  if (abstand === 0 && minuten < 6 * 60) return `vor ${Math.floor(minuten / 60)} Std.`;
  if (abstand === 0) return `heute ${UHR.format(t)}`;
  if (abstand === -1) return `gestern ${UHR.format(t)}`;
  const [j, m, d] = tag.split("-");
  return j === heute(jetzt).slice(0, 4) ? `${d}.${m}.` : `${d}.${m}.${j}`;
}

/** Wohin ein Klick auf eine Mitteilung führt (KOPFZEILE.md, Abschnitt 3.4). Ohne Link: null, dann nur gelesen setzen.
 *  - Seite: wie bisher, die Aufgabe öffnet als Dialog über /mitteilungen, Schließen geht einen Schritt zurück.
 *  - Leiste: Die Leiste ist nur eine Schicht über der Seite. Der Eintrag ?mitteilungen wird ersetzt, die Aufgabe übernimmt seinen
 *    Verlaufsmerker. So schließt Schließen die Aufgabe und landet auf der Seite darunter, nicht wieder in der Leiste. */
export function klickZiel(link: string | null, suche: string, ueber: "seite" | "leiste", zustand: unknown): { to: To; replace: boolean; state: unknown } | null {
  const ziel = mitteilungZiel(link, suche);
  if (!ziel) return null;
  if (ueber === "leiste") return { to: ziel.to, replace: true, state: ziel.dialog ? zustand : undefined };
  return { to: ziel.to, replace: false, state: ziel.dialog ? DIALOG_GEOEFFNET : undefined };
}

/** Ort einer Mitteilung (Sprint oder Vorhaben). Ein Modul liefert ihn als Hook (Modul.mitteilungOrte), die Shell reicht die Hooks
 *  über diesen Kontext an die Liste. So kennt der Kern das Modul aufgaben nicht. */
export type OrtQuelle = (links: readonly string[]) => ReadonlyMap<string, string>;
export const MitteilungOrtKontext = createContext<readonly OrtQuelle[]>([]);
